// When applicable this component shows a count of contacts missing emails or phone numbers

import { useNavigate, useParams } from "react-router";
import { useMemo } from "react";
import { GridFilterItem } from "@mui/x-data-grid";
import {
  ContactStatus,
  policiesNeedingEmail,
  PolicyType,
} from "shared";

import { useCurrentGroup } from "../../hooks/useGroups";
import { Notification } from "../../../common/Atoms/Notification";
import { Button } from "../../../common/Atoms/Button";
import { useContactTableModels } from "../../hooks/useContacts";

export function useContactDataWarnings() {
  const { policyId, organisationId } = useParams();
  const group = useCurrentGroup();
  const { setFilterModel } = useContactTableModels();
  const navigate = useNavigate();

  const currentPolicy = useMemo(() => {
    if (!group?.policies) return null;

    return group.policies.find(p => p.id === Number(policyId));
  }, [group, policyId]);

  const mobileWarning = useMemo(() => {
    if (group.contactsNoMobileCount === 0) return false;

    if (currentPolicy) {
      return currentPolicy.policy_type === PolicyType.SMS;
    }

    if (!group.policies) return false;

    return group.policies.some(p => p.policy_type === PolicyType.SMS);
  }, [group, currentPolicy]);

  const emailWarning = useMemo(() => {
    if (group.contactsNoEmailCount === 0) return false;

    if (currentPolicy) {
      return policiesNeedingEmail.includes(currentPolicy.policy_type);
    }

    if (!group.policies) return false;

    return group.policies.some(p => policiesNeedingEmail.includes(p.policy_type));
  }, [group, currentPolicy]);

  function navigateToContacts(items: GridFilterItem[]) {
    setFilterModel({
      items: [
        ...items,
        {
          field: `status`,
          operator: `is`,
          value: ContactStatus.ACTIVE,
          id: Math.round(Math.random() * 1e5),
        },
        {
          field: `paidniceGroup`,
          operator: `is`,
          value: group.groupFormData.id,
          id: Math.round(Math.random() * 1e5),
        },
      ],
    });

    navigate(`/${organisationId}/contacts/manage?persist=true`);
  }

  function onClickNoEmail() {
    navigateToContacts([{ field: `email`, operator: `isEmpty`, id: Math.round(Math.random() * 1e5) }]);
  }

  function onClickNoPhone() {
    navigateToContacts([{ field: `mobileNumber`, operator: `isEmpty`, id: Math.round(Math.random() * 1e5) }]);
  }

  return {
    group,
    currentPolicy,
    emailWarning,
    mobileWarning,
    onClickNoEmail,
    onClickNoPhone,
  };
}

export function ContactDataWarning() {
  const {
    group,
    currentPolicy,
    emailWarning,
    mobileWarning,
    onClickNoEmail,
    onClickNoPhone,
  } = useContactDataWarnings();

  const notifications = useMemo(() => {
    const notifications: {
      message: string;
      onClick: () => void;
      buttonText: string;
    }[] = [];

    if (!group) return notifications;
    const suffix = currentPolicy ? ` for this policy` : ` for some of the policies in this group`;

    if (emailWarning) {
      notifications.push({
        message: `${ group.contactsNoEmailCount } contact${ group.contactsNoEmailCount > 1 ? `s in this group are` : ` in this group is` } missing an email address, which may be required ${suffix}.`,
        onClick: onClickNoEmail,
        buttonText: `View contacts`,
      });
    }

    if (mobileWarning) {
      notifications.push({
        message: `${ group.contactsNoMobileCount } contact${ group.contactsNoMobileCount > 1 ? `s in this group are` : ` in this group is` } missing a mobile number, which may be required ${suffix}.`,
        onClick: onClickNoPhone,
        buttonText: `View contacts`,
      });
    }

    return notifications;
  }, [emailWarning, mobileWarning, group, currentPolicy]);

  if (!group) return null;

  if (!group.contactsNoEmailCount && !group.contactsNoMobileCount) return null;

  if (!notifications.length) return null;

  return (
    <div className={ `space-y-4` }>
      {
        notifications.map((n, i) => {
          return (
            <Notification
              key={ i + n.message }
              type={ `warning` }
              buttons={ (
                <Button
                  onClick={ n.onClick }
                  color={ `orangeOutline` }
                  size={ `sm` }
                >
                  { n.buttonText }
                </Button>
              ) }
            >
              { n.message }
            </Notification>
          );
        })
      }
    </div>
  );
}
